import { useMemo, type CSSProperties } from 'react'
import { encode } from 'uqr'
import { qrPath } from './qrPath.ts'
import styles from './QrCode.module.css'

export type QrCodeEcc = 'L' | 'M' | 'Q' | 'H'

export interface QrCodeProps {
  /** Text to encode — usually a URL. */
  value: string
  /** Rendered width and height in px, quiet zone included. Default 192. */
  size?: number
  /**
   * Error-correction level: how much of the symbol can be damaged or covered
   * and still scan (L 7%, M 15%, Q 25%, H 30%). Higher levels need more
   * modules for the same value. Default 'M'.
   */
  ecc?: QrCodeEcc
  /** Accessible name — say what scanning it does, not "QR code". */
  label: string
  className?: string
}

export function QrCode({ value, size = 192, ecc = 'M', label, className }: QrCodeProps) {
  // border: 0 — qrPath owns the quiet zone so web and mobile draw the same margin.
  const { d, size: modules } = useMemo(
    () => qrPath(encode(value, { ecc, border: 0 }).data),
    [value, ecc],
  )

  return (
    <div
      className={`${styles.qr}${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={label}
      // The size rides as a custom property on this <div> and reaches the <svg>
      // through CSS, never as an inline style on the svg itself. React writes
      // inline styles through `element.style`, and a browser with SVG turned
      // off (Firefox's `svg.disabled`) hands back an Element with no `.style` —
      // React throws mid-render and the error boundary blanks the page.
      style={{ '--qr-size': `${size}px` } as CSSProperties}
    >
      <svg
        className={styles.svg}
        viewBox={`0 0 ${modules} ${modules}`}
        aria-hidden="true"
        shapeRendering="crispEdges"
      >
        <rect className={styles.light} width={modules} height={modules} />
        <path className={styles.dark} d={d} />
      </svg>
    </div>
  )
}
